import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { fmtTime } from '../utils/movie';
import { centerAt } from '../utils/social';

const COUNT_OPTIONS = [4, 6, 8, 12];
const THUMB_W = 480;

// Interpolate a detected track's box at master time t (mirrors the studio).
function interpTrack(track, t) {
  const s = (track && track.samples) || [];
  if (!s.length) return null;
  if (t < s[0].t - 0.6 || t > s[s.length - 1].t + 0.6) return null;
  if (t <= s[0].t) return s[0];
  const last = s[s.length - 1];
  if (t >= last.t) return last;
  for (let i = 1; i < s.length; i += 1) {
    if (t <= s[i].t) {
      const a = s[i - 1];
      const b = s[i];
      const f = (t - a.t) / (b.t - a.t || 1);
      return {
        cx: a.cx + (b.cx - a.cx) * f,
        cy: a.cy + (b.cy - a.cy) * f,
        w: a.w + (b.w - a.w) * f,
        h: a.h + (b.h - a.h) * f,
      };
    }
  }
  return last;
}

// Per-clip "frames with tracking" contact sheet for the Reframe tab. Captures N
// evenly-spaced frames from the composed master across ONE clip's master-time
// window, then overlays every detected subject box + the chosen center on each.
// This is the place to fix mistracking: click a box to lock THAT subject for the
// clip, or click anywhere to drop the center there. Click the timecode to jump
// the main preview to that frame.
//
// Props:
//   url           master source (HLS .m3u8 or presigned/CDN MP4)
//   streamType    'hls' to force hls.js (else inferred)
//   srcW, srcH    master dims (for box aspect; only the ratio matters here)
//   tileStart/End the clip's window in master seconds
//   clipKey       active clip id — re-captures when it changes
//   tracks        movie-wide YOLO tracks [{ id, samples:[{t,cx,cy,w,h}] }]
//   subjectTrackId the clip's currently chosen subject (highlighted)
//   waypoints     the clip's center path [{ t, cx, cy }] in master seconds
//   trackColor    (i) => css color, aligned to `tracks` order
//   onSeek        (masterSec) => void — jump the main preview
//   onLockSubject (trackId) => void — re-tag the best subject for this clip
//   onSetCenter   (masterSec, cx, cy) => void — drop/replace the center here
export default function FrameTrackStrip({
  url,
  streamType,
  tileStart = 0,
  tileEnd = 0,
  clipKey,
  tracks = [],
  subjectTrackId,
  waypoints = [],
  trackColor,
  onSeek,
  onLockSubject,
  onSetCenter,
}) {
  const [count, setCount] = useState(6);
  const [frames, setFrames] = useState([]); // [{ t, src }]
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [nonce, setNonce] = useState(0);
  const cellRefs = useRef({});

  useEffect(() => {
    if (!url || !(tileEnd > tileStart)) { setFrames([]); setStatus('idle'); return undefined; }
    let cancelled = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    let hls = null;

    setStatus('loading');
    setProgress(0);
    setFrames([]);

    const cleanup = () => {
      try { if (hls) hls.destroy(); } catch { /* noop */ }
      try { video.removeAttribute('src'); video.load(); } catch { /* noop */ }
    };

    const waitSeek = (t) => new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        video.removeEventListener('seeked', finish);
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      };
      video.addEventListener('seeked', finish);
      try { video.currentTime = t; } catch { finish(); }
      setTimeout(finish, 1500);
    });

    const grab = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return null;
      const cw = Math.min(THUMB_W, w);
      const ch = Math.max(1, Math.round((cw * h) / w));
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      try {
        canvas.getContext('2d').drawImage(video, 0, 0, cw, ch);
        return canvas.toDataURL('image/jpeg', 0.82);
      } catch {
        return null;
      }
    };

    const run = async () => {
      const lo = Math.max(0, tileStart);
      const hi = Math.max(lo + 0.05, tileEnd);
      const span = Math.max(0.001, hi - lo);
      const out = [];
      for (let i = 0; i < count; i += 1) {
        if (cancelled) return;
        const t = lo + span * ((i + 0.5) / count);
        // eslint-disable-next-line no-await-in-loop
        await waitSeek(Math.min(t, hi - 0.001));
        if (cancelled) return;
        const src = grab();
        if (src) {
          out.push({ t, src });
          setFrames(out.slice());
        }
        setProgress((i + 1) / count);
      }
      if (!cancelled) {
        setStatus(out.length ? 'ready' : 'error');
        cleanup();
      }
    };

    const onMeta = () => { run().catch(() => { if (!cancelled) setStatus('error'); }); };
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('error', () => { if (!cancelled) setStatus('error'); });

    const isHls = streamType === 'hls' || /\.m3u8(\?|$)/.test(url);
    if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data?.fatal && !cancelled) setStatus('error'); });
    } else {
      video.src = url;
      video.load();
    }

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onMeta);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, streamType, tileStart, tileEnd, clipKey, count, nonce]);

  const bgClick = (e, t, idx) => {
    if (!onSetCenter) return;
    const el = cellRefs.current[idx];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const cy = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
    onSetCenter(t, cx, cy);
  };

  return (
    <div className="cs-filmstrip">
      <div className="cs-filmstrip-bar">
        <span className="cs-filmstrip-title">Frames + tracking</span>
        <span className="cs-help" style={{ flex: '1 1 auto', fontSize: 12 }}>
          This clip’s frames with every detected subject. Click a box to lock that subject · click
          elsewhere to drop the center · click the time to jump.
        </span>
        <label className="cs-help" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          Frames
          <select
            className="cs-input cs-input-sm"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            style={{ width: 64 }}
          >
            {COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          className="cs-btn cs-btn-ghost cs-btn-sm"
          onClick={() => setNonce((n) => n + 1)}
          disabled={status === 'loading' || !url}
          title="Re-grab the sample frames"
        >
          ↻ Refresh
        </button>
      </div>

      {status === 'error' && (
        <div className="cs-empty" style={{ fontSize: 13 }}>Couldn’t sample frames from the master.</div>
      )}

      {(status === 'loading' || frames.length > 0) && (
        <div className="cs-filmstrip-grid">
          {frames.map((f, i) => {
            const c = centerAt(waypoints, f.t);
            return (
              <div
                key={`${clipKey}-${i}-${f.t.toFixed(3)}`}
                className="cs-filmstrip-cell"
                ref={(el) => { cellRefs.current[i] = el; }}
                style={{ position: 'relative', cursor: 'crosshair' }}
                onClick={(e) => bgClick(e, f.t, i)}
                title="Click to set the center here for this frame"
              >
                <img src={f.src} alt="" draggable={false} style={{ width: '100%', height: 'auto', display: 'block' }} />
                <svg
                  className="cs-studio-overlay"
                  viewBox="0 0 1000 1000"
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                >
                  {tracks.map((tr, ti) => {
                    const b = interpTrack(tr, f.t);
                    if (!b) return null;
                    const isSub = tr.id === subjectTrackId;
                    const col = trackColor ? trackColor(ti) : '#2dd4bf';
                    return (
                      <rect
                        key={tr.id}
                        x={(b.cx - b.w / 2) * 1000}
                        y={(b.cy - b.h / 2) * 1000}
                        width={b.w * 1000}
                        height={b.h * 1000}
                        style={{
                          stroke: col,
                          strokeWidth: isSub ? 4 : 2.5,
                          fill: isSub ? `${col}28` : `${col}10`,
                          cursor: 'pointer',
                        }}
                        onClick={(e) => { e.stopPropagation(); if (onLockSubject) onLockSubject(tr.id); }}
                      >
                        <title>{`Lock subject ${ti + 1} for this clip`}</title>
                      </rect>
                    );
                  })}
                  {/* Chosen center crosshair */}
                  <g style={{ pointerEvents: 'none' }}>
                    <circle cx={c.cx * 1000} cy={c.cy * 1000} r="16" style={{ fill: 'none', stroke: '#fff', strokeWidth: 3 }} />
                    <line x1={c.cx * 1000 - 26} y1={c.cy * 1000} x2={c.cx * 1000 + 26} y2={c.cy * 1000} style={{ stroke: '#fff', strokeWidth: 3 }} />
                    <line x1={c.cx * 1000} y1={c.cy * 1000 - 26} x2={c.cx * 1000} y2={c.cy * 1000 + 26} style={{ stroke: '#fff', strokeWidth: 3 }} />
                  </g>
                </svg>
                <button
                  type="button"
                  className="cs-filmstrip-tc"
                  onClick={(e) => { e.stopPropagation(); if (onSeek) onSeek(f.t); }}
                  title="Jump the main preview to this frame"
                  style={{ cursor: 'pointer', border: 'none', pointerEvents: 'auto' }}
                >
                  {fmtTime(f.t)} ↧
                </button>
              </div>
            );
          })}
          {status === 'loading' && Array.from({ length: Math.max(0, count - frames.length) }).map((_, i) => (
            <div key={`ph-${i}`} className="cs-filmstrip-cell cs-filmstrip-ph">
              <span className="cs-spinner" />
            </div>
          ))}
        </div>
      )}

      {status === 'loading' && (
        <div className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
          Sampling frames… {Math.round(progress * 100)}%
        </div>
      )}
    </div>
  );
}
